import React from 'react';
import { Link } from 'gatsby';
import { formatUrl } from '@/utils/url';
import { getTranslatableSlug } from '@/utils/translatable-slug';
import { useCurrentLocaleData } from '@/utils/localisation';

/**
 * Get the formatted URL for a Storyblok link object.
 *
 * @param {object} link - Storyblok link object
 * @param {ReturnType<typeof useCurrentLocaleData>} currentLocaleData - Output of useCurrentLocaleData hook
 * @param {boolean} includeDomain - Whether to include the domain in the URL
 * @returns {string} - Formatted URL
 */
export const getLinkPath = (link, currentLocaleData, includeDomain = false) => {
	const externalLink = link.linktype === 'url';
	const linkWithAnchor = link.anchor ? `${link.cached_url}#${link.anchor}` : link.cached_url;
	let linkPath = link.linktype === 'url' ? link.url : linkWithAnchor;

	if (link.story) {
		const linkLang = link.story.lang;
		const translatedSlug = getTranslatableSlug(link.story, linkLang);

		if (translatedSlug) {
			linkPath = `${linkLang}/${translatedSlug.path}`;
		}
	}

	if (externalLink) {
		const { locale, language } = currentLocaleData;

		return linkPath.replace('{locale}', locale).replace('{language}', language);
	}

	return formatUrl(linkPath, includeDomain);
};

function LinkComponent({ link, appearance, openNewTab, children, additionalProps = {} }) {
	const target = openNewTab ? '_blank' : '_self';
	let externalLink = link.linktype === 'url';
	let linkPath = getLinkPath(link, useCurrentLocaleData());

	const preventDefault = e => {
		e.preventDefault();
	};

	// Add smooth scrolling for anchor links
	const handleAnchorClick = e => {
		let shouldScroll = false;

		try {
			const originUrl = new URL(window.location.href);
			const targetUrl = new URL(linkPath, window.location.origin);
			shouldScroll = originUrl.host === targetUrl.host && originUrl.pathname === targetUrl.pathname && targetUrl.hash;
		} catch (e) {
			// If URL parsing fails, fallback to browser native behavior
			console.error('Could not determine if smooth scrolling should be applied:', e);
		}

		if (shouldScroll) {
			e.preventDefault();
			const targetId = linkPath.includes('#') ? linkPath.split('#')[1] : linkPath.substring(1);
			const targetElement = document.getElementById(targetId);

			if (targetElement) {
				targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			}
		}

		if (additionalProps.onClick) {
			additionalProps.onClick(e);
		}
	};

	const jsHandlers = {};
	if (linkPath === '') {
		jsHandlers.onClick = preventDefault;
	} else if (linkPath.startsWith('#') || linkPath.includes('#')) {
		jsHandlers.onClick = handleAnchorClick;
	}

	// Use relative path for gynzy.com links
	if (linkPath.startsWith('https://www.gynzy.com/')) {
		linkPath = linkPath.replace('https://www.gynzy.com/', '/');
	}

	const oldBoardUrlRegex = /\/teach\/[a-z-]{2,5}\/board\//gi;
	if (linkPath.match(oldBoardUrlRegex)) {
		const parts = linkPath.split('/teach/');

		externalLink = true;
		linkPath = `https://teacher.gynzy.com/${parts[1]}`;
	}

	if (externalLink) {
		return (
			<a href={linkPath} className={appearance} target={target} rel="nofollow" {...additionalProps} {...jsHandlers}>
				{children}
			</a>
		);
	}

	return (
		<Link to={linkPath} className={appearance} target={target} {...additionalProps} {...jsHandlers}>
			{children}
		</Link>
	);
}

export default LinkComponent;
